/**
 * VoterFilterPanel — Floating filter button + dropdown for voter map filtering.
 *
 * Positioned adjacent to TurfListPanel. Opens a Popover with 7 filter types.
 * Filter state lives in fieldOpsStore (Zustand) — persists during session.
 */

import { useCallback, useMemo, useState } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { useFieldOpsStore } from '@/stores/fieldOpsStore';
import { useVoterFilterOptions, useTags } from '@/hooks/queries';
import { countActiveFilters } from '@signalvote/shared';
import type { VoterMapFilter } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

const SUPPORT_LEVELS = [
  { value: 'StrongYes', label: 'Strong Yes' },
  { value: 'LeaningYes', label: 'Leaning Yes' },
  { value: 'Undecided', label: 'Undecided' },
  { value: 'LeaningNo', label: 'Leaning No' },
  { value: 'StrongNo', label: 'Strong No' },
  { value: 'Unknown', label: 'Unknown' },
] as const;

const ZIP_RESULT_LIMIT = 25;

export function VoterFilterPanel() {
  const voterFilters = useFieldOpsStore((s) => s.voterFilters);
  const setVoterFilters = useFieldOpsStore((s) => s.setVoterFilters);
  const clearVoterFilters = useFieldOpsStore((s) => s.clearVoterFilters);
  const [zipSearch, setZipSearch] = useState('');

  const { data: filterOptions } = useVoterFilterOptions();
  const { data: tags } = useTags();

  const filterCount = countActiveFilters(voterFilters);
  const zipOptions = filterOptions?.zipCodes ?? [];
  const selectedZipCodes = voterFilters.zipCodes ?? [];
  const filteredZipOptions = useMemo(() => {
    const query = zipSearch.trim().toLowerCase();
    const matches = query
      ? zipOptions.filter((zip) => zip.toLowerCase().includes(query))
      : zipOptions;
    return matches.filter((zip) => !selectedZipCodes.includes(zip));
  }, [selectedZipCodes, zipOptions, zipSearch]);
  const visibleZipOptions = filteredZipOptions.slice(0, ZIP_RESULT_LIMIT);
  const hiddenZipOptionCount = Math.max(0, filteredZipOptions.length - visibleZipOptions.length);

  const updateFilter = useCallback(
    (patch: Partial<VoterMapFilter>) => {
      setVoterFilters(patch);
    },
    [setVoterFilters],
  );

  const toggleChip = useCallback(
    (key: 'zipCodes' | 'party' | 'support' | 'education', value: string) => {
      const current = voterFilters[key] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      updateFilter({ [key]: next });
    },
    [voterFilters, updateFilter],
  );

  const toggleTag = useCallback(
    (tagId: number) => {
      const current = voterFilters.tagIds ?? [];
      const next = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId];
      updateFilter({ tagIds: next });
    },
    [voterFilters, updateFilter],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-2 rounded-full bg-white/95 backdrop-blur-sm shadow-[0_2px_12px_rgba(0,0,0,0.1)] hover:bg-white hover:shadow-md transition-all duration-200',
            filterCount > 0 && 'ring-2 ring-primary',
          )}
        >
          <Filter className="h-4 w-4" />
          <span className="text-xs">
            Filters{filterCount > 0 ? ` (${filterCount})` : ''}
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-80 max-h-[70vh] overflow-y-auto p-0"
      >
        <div className="p-4 space-y-4">
          {/* Contact Status */}
          <FilterSection title="Contact Status">
            <div className="flex gap-1">
              {[
                { value: undefined, label: 'All' },
                { value: 'not-contacted' as const, label: 'Not Contacted' },
                { value: 'contacted' as const, label: 'Contacted' },
              ].map(({ value, label }) => (
                <button
                  key={label}
                  onClick={() => updateFilter({ contactStatus: value })}
                  className={cn(
                    'px-3 py-1 text-xs rounded-full border transition-colors',
                    voterFilters.contactStatus === value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </FilterSection>

          {/* ZIP Code */}
          <FilterSection title="ZIP Code">
            <div className="space-y-2">
              {selectedZipCodes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedZipCodes.map((zip) => (
                    <ChipButton
                      key={zip}
                      label={zip}
                      selected
                      onClick={() => toggleChip('zipCodes', zip)}
                    />
                  ))}
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={zipSearch}
                  onChange={(e) => setZipSearch(e.target.value)}
                  placeholder="Search ZIPs"
                  className="h-8 w-full rounded-md border border-gray-200 bg-white pl-7 pr-7 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                {zipSearch && (
                  <button
                    type="button"
                    onClick={() => setZipSearch('')}
                    aria-label="Clear ZIP search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border border-gray-100">
                {visibleZipOptions.map((zip) => (
                  <button
                    key={zip}
                    onClick={() => toggleChip('zipCodes', zip)}
                    className="flex h-8 w-full items-center justify-between border-b border-gray-50 px-2.5 text-left text-xs text-gray-700 last:border-b-0 hover:bg-gray-50"
                  >
                    <span>{zip}</span>
                  </button>
                ))}
                {zipOptions.length === 0 && (
                  <div className="px-2.5 py-2 text-xs text-gray-400">No ZIP codes available</div>
                )}
                {zipOptions.length > 0 && filteredZipOptions.length === 0 && (
                  <div className="px-2.5 py-2 text-xs text-gray-400">No ZIP codes found</div>
                )}
              </div>
              {hiddenZipOptionCount > 0 && (
                <div className="text-[10px] text-gray-400">
                  {hiddenZipOptionCount} more match{hiddenZipOptionCount === 1 ? '' : 'es'}
                </div>
              )}
            </div>
          </FilterSection>

          {/* Party */}
          <FilterSection title="Party">
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {(filterOptions?.partyAffiliations ?? []).map((party) => (
                <ChipButton
                  key={party}
                  label={party}
                  selected={voterFilters.party?.includes(party) ?? false}
                  onClick={() => toggleChip('party', party)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Vote Frequency */}
          <FilterSection title="Vote Frequency">
            <div className="px-1">
              <Slider
                value={[
                  voterFilters.voteFrequencyMin ?? 0,
                  voterFilters.voteFrequencyMax ?? 8,
                ]}
                min={0}
                max={8}
                step={1}
                onValueChange={([min, max]: number[]) => {
                  updateFilter({
                    voteFrequencyMin: min === 0 ? undefined : min,
                    voteFrequencyMax: max === 8 ? undefined : max,
                  });
                }}
              />
              <div className="flex justify-between mt-1 text-[10px] text-gray-400">
                <span>0</span>
                <span>4</span>
                <span>8+</span>
              </div>
            </div>
          </FilterSection>

          {/* Support Level */}
          <FilterSection title="Support Level">
            <div className="flex flex-wrap gap-1.5">
              {SUPPORT_LEVELS.map(({ value, label }) => (
                <ChipButton
                  key={value}
                  label={label}
                  selected={voterFilters.support?.includes(value) ?? false}
                  onClick={() => toggleChip('support', value)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Education */}
          <FilterSection title="Education">
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {(filterOptions?.educationLevels ?? []).map((edu) => (
                <ChipButton
                  key={edu}
                  label={edu}
                  selected={voterFilters.education?.includes(edu) ?? false}
                  onClick={() => toggleChip('education', edu)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Donor Type */}
          <FilterSection title="Donor Type">
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={voterFilters.conservativeDonor === true}
                  onChange={(e) =>
                    updateFilter({
                      conservativeDonor: e.target.checked ? true : undefined,
                    })
                  }
                  className="h-4 w-4 rounded border-gray-300 accent-primary"
                />
                <span className="text-xs">Conservative Donor</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={voterFilters.liberalDonor === true}
                  onChange={(e) =>
                    updateFilter({
                      liberalDonor: e.target.checked ? true : undefined,
                    })
                  }
                  className="h-4 w-4 rounded border-gray-300 accent-primary"
                />
                <span className="text-xs">Liberal Donor</span>
              </label>
            </div>
          </FilterSection>

          {/* Tags */}
          <FilterSection title="Tags">
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {(tags ?? []).map((tag) => (
                <ChipButton
                  key={tag.id}
                  label={tag.tagName}
                  selected={voterFilters.tagIds?.includes(tag.id) ?? false}
                  onClick={() => toggleTag(tag.id)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Clear all */}
          {filterCount > 0 && (
            <div className="pt-2 border-t border-gray-100">
              <button
                onClick={clearVoterFilters}
                className="text-xs text-primary hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {title}
      </div>
      {children}
    </div>
  );
}

function ChipButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 text-xs rounded-full border transition-colors',
        selected
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400',
      )}
    >
      {label}
    </button>
  );
}
